/**
 * Score bands — single source of truth for turning a 0–100 score into a band
 * label and colour.
 *
 * Overall scores map evenly onto the five `ScoreValue` bands, reusing the band
 * labels from @evaltick/shared. Colour is keyed off the band so the same score
 * renders identically everywhere it appears: the overall band tag
 * (AssessmentSummary), the per-question bars (EvalChart), and the dimension
 * bars (DimensionBreakdown).
 *
 * The colour ramp collapses the five bands onto three semantic colours at the
 * 40 / 60 band boundaries — red for the bottom two bands, amber for the middle,
 * green for the top two — so a bar's colour never contradicts its band label.
 */
import { ScoreLabel } from '@evaltick/shared';

const SCORE_BAND_LABELS: readonly string[] = Object.values(ScoreLabel);
const POINTS_PER_BAND = 100 / SCORE_BAND_LABELS.length;

// One entry per band (lowest → highest). CSS-var colours drive Progress
// `strokeColor` and band-label text; the Ant Design Tag `color` tokens drive
// the summary band tag. Both are indexed by the same band, so they can never
// disagree for a given score.
const SCORE_BAND_COLORS: readonly string[] = [
  'var(--color-red)',
  'var(--color-orange)',
  'var(--color-yellow)',
  'var(--color-green-2)',
  'var(--color-green)',
];
const SCORE_BAND_TAG_COLORS: readonly string[] = ['error', 'error', 'warning', 'success', 'success'];

export const scoreToBandIndex = (score: number): number =>
  Math.min(Math.max(Math.ceil(score / POINTS_PER_BAND) - 1, 0), SCORE_BAND_LABELS.length - 1);

export const scoreToLabel = (score: number): string => SCORE_BAND_LABELS[scoreToBandIndex(score)];

/** CSS-var colour for progress-bar strokes and band-label text. */
export const scoreToColor = (score: number): string => SCORE_BAND_COLORS[scoreToBandIndex(score)];

/** Ant Design Tag `color` token for the summary band tag. */
export const scoreToTagColor = (score: number): string => SCORE_BAND_TAG_COLORS[scoreToBandIndex(score)];

export type ScoreTone =
  | 'unsuitable'
  | 'developing'
  | 'competent'
  | 'strong'
  | 'exceptional'
  | 'neutral';

const SCORE_BAND_TONES: readonly ScoreTone[] = [
  'unsuitable',
  'developing',
  'competent',
  'strong',
  'exceptional',
];

export const scoreToTone = (score: number | null | undefined): ScoreTone =>
  score == null || score <= 0 ? 'neutral' : SCORE_BAND_TONES[scoreToBandIndex(score)];

const TONE_CLASS_KEY: Record<ScoreTone, string> = {
  unsuitable: 'toneUnsuitable',
  developing: 'toneDeveloping',
  competent: 'toneCompetent',
  strong: 'toneStrong',
  exceptional: 'toneExceptional',
  neutral: 'toneNeutral',
};

export const toneClass = (styles: Record<string, string>, score: number | null | undefined): string =>
  styles[TONE_CLASS_KEY[scoreToTone(score)]];

